// Text the built-in jsPDF fonts can print. Shared by every jsPDF report.
//
// jsPDF 1.5.3 writes the standard fonts (Helvetica etc.) as WinAnsi: U+0000–U+00FF
// plus the 27 cp1252 characters in WIN_ANSI_EXTRAS (checked against the library,
// 2026-10-01). If a string holds ANY other character, jsPDF writes the whole string
// as UTF-16 bytes and it prints as garbage, e.g. "Ø=ÝT" for each 🕔 / 📊 / 📍 icon in
// alert AL-00885's drain event rows. In a table cell, one such character garbles
// the whole value.
//
// toPdfText() makes a string printable:
//  - Emoji are removed: pictographs U+1F000–U+1FAFF, any symbol written with the emoji
//    selector U+FE0F (⚠️), and the invisible joiners/selectors that build emoji. In
//    system text they are icons beside a value; the value itself is kept.
//  - Invisible formatting characters (zero-width space, BOM, direction marks) are removed.
//  - Every other character the font cannot print (₹, →, ✓, non-Latin scripts) becomes
//    UNPRINTABLE_MARK and is counted, so the gap is visible in the PDF and to the user.
// The source data is never changed; AssetPro still shows the original text.
// See docs/pdf-unprintable-characters.md.

export const UNPRINTABLE_MARK = '[?]'
const EMOJI_SELECTOR = '\uFE0F'
const WIN_ANSI_EXTRAS = new Set([
  0x20AC, 0x201A, 0x0192, 0x201E, 0x2026, 0x2020, 0x2021, 0x02C6, 0x2030, 0x0160, 0x2039, 0x0152, 0x017D,
  0x2018, 0x2019, 0x201C, 0x201D, 0x2022, 0x2013, 0x2014, 0x02DC, 0x2122, 0x0161, 0x203A, 0x0153, 0x017E, 0x0178
])
const isDroppedChar = (cp) =>
  (cp >= 0x1F000 && cp <= 0x1FAFF) || // emoji pictographs, skin tones, flag letters
  (cp >= 0xFE00 && cp <= 0xFE0F) ||   // variation selectors
  (cp >= 0xE0020 && cp <= 0xE007F) || // emoji tag sequences
  cp === 0x20E3 ||                    // keycap
  (cp >= 0x200B && cp <= 0x200F) ||   // zero-width space / joiners, direction marks
  cp === 0x2060 || cp === 0xFEFF      // word joiner, BOM

export const toPdfText = (value) => {
  const chars = Array.from(value === null || value === undefined ? '' : String(value)) // code points
  let unprintable = 0
  const text = chars.map((ch, i) => {
    const cp = ch.codePointAt(0)
    if (cp <= 0xFF || WIN_ANSI_EXTRAS.has(cp)) return ch
    if (isDroppedChar(cp) || chars[i + 1] === EMOJI_SELECTOR) return ''
    unprintable += 1
    return UNPRINTABLE_MARK
  }).join('')
  return { text, unprintable }
}

// One tracker per PDF. Pass every data-bearing string through clean(), give every
// autoTable `didParseCell: pdf.cellHook(existingHook)`, and read count() at the end.
export const createPdfTextTracker = () => {
  let unprintable = 0
  const clean = (value) => {
    const r = toPdfText(value)
    unprintable += r.unprintable
    return r.text
  }
  return {
    clean,
    // jspdf-autotable calls didParseCell once per head/body/foot cell, before column
    // widths are measured, so the cleaned text is what gets measured and drawn.
    // `hook` is the table's own didParseCell, if it has one; it still sees cell.raw.
    cellHook: (hook) => (data) => {
      data.cell.text = [].concat(data.cell.text).map(clean)
      if (hook) hook(data)
    },
    count: () => unprintable
  }
}

// Wording shared by the PDF note and the download toast.
export const unprintableNote = (count) =>
  `${count} character(s) shown as ${UNPRINTABLE_MARK} in this report cannot be printed in the PDF font. ` +
  'The original text is in AssetPro.'
export const unprintableToast = (count) =>
  `${count} character(s) could not be printed in the PDF and show as ${UNPRINTABLE_MARK}. See the note at the end of the PDF.`
